import { instantMeiliSearch } from '@meilisearch/instant-meilisearch';
import instantsearch from 'instantsearch.js';
import {
    clearRefinements,
    configure,
    currentRefinements,
    infiniteHits,
    menuSelect,
    panel,
    refinementList,
    sortBy,
    stats,
    toggleRefinement,
} from 'instantsearch.js/es/widgets';
import { de } from './search/translations';
import { searchBoxWithLabelWidget } from './search/widgets/searchBoxWithLabel';
import { eventTrackingMiddlewareFactory } from './search/event-tracking-middleware';
import { itemTemplatesBySection } from './search/templates/hits';

const { url, apiKey, indexName } = window.MeiliSearchConfig;
const { searchClient } = instantMeiliSearch(url, apiKey);

const initialState = JSON.parse(window.initialState);

const urlParams = new URLSearchParams(window.location.search);
const query = urlParams.get('q');
if (query) {
    initialState.query = query;
}

const search = instantsearch({
    searchClient,
    indexName,
    initialUiState: {
        [indexName]: initialState,
    },
    future: {
        preserveSharedStateOnUnmount: true,
    },
});

/**
 * Search middleware that will clear any refinements that are no longer
 * applicable after the section filter is changed.
 */
const clearInapplicableRefinementsOnSectionReset = ({ instantSearchInstance }) => {
    const ALWAYS_VISIBLE_FILTERS = ['topics.title', 'trade_sectors'];

    return {
        onStateChange({ uiState }) {
            const hasSectionFilter =
                instantSearchInstance.helper.state.hierarchicalFacetsRefinements.section?.length >
                0;
            if (hasSectionFilter) {
                return;
            }

            let filtersWereRemoved = false;
            instantSearchInstance.helper.clearRefinements(function (value, attribute, type) {
                const isAlwaysVisibleFilter = ALWAYS_VISIBLE_FILTERS.includes(attribute);
                if (isAlwaysVisibleFilter) {
                    return false;
                }

                const remove = [
                    'disjunctiveFacet',
                    'conjunctiveFacet',
                    'hierarchicalFacet',
                ].includes(type);
                if (remove) {
                    filtersWereRemoved = true;
                }
                return remove;
            });

            if (filtersWereRemoved) {
                instantSearchInstance.helper.search();
            }
        },

        subscribe() {},

        unsubscribe() {},
    };
};

/**
 * Create a panel configuration with common hidden logic and header template.
 * @param {string} attribute        The attribute to refine.
 * @param {?array} relatedSections  Only show the panel if one of those sections is selected.
 */
const makePanelConfig = (attribute, relatedSections = null) => ({
    hidden: options => {
        if (!options.canRefine) {
            return true;
        }

        if (!relatedSections) {
            return false;
        }

        const relatedSectionIsSelected = options.state.hierarchicalFacetsRefinements.section?.some(
            section => relatedSections.includes(section),
        );
        return !relatedSectionIsSelected;
    },
    templates: {
        header(options, { html }) {
            return html`${de[attribute] || attribute}`;
        },
    },
});

/**
 * Create a refinement list panel.
 * @param {string} attribute        The attribute to refine.
 * @param {?array} relatedSections  Only show the panel if one of those sections is selected.
 */
const makeRefinementListPanel = (attribute, relatedSections = null) =>
    panel(makePanelConfig(attribute, relatedSections))(refinementList)({
        attribute,
        container: document.querySelector(`[data-attribute="${attribute}"]`),
        limit: 6,
        showMore: true,
        sortBy: ['count:desc', 'name:asc'],
        templates: {
            showMoreText(data, { html }) {
                return html` <span
                    class="facet-search__show-more ${data.isShowingMore
                        ? 'facet-search__show-more--open'
                        : 'facet-search__show-more--closed'}"
                    >${data.isShowingMore ? 'Weniger anzeigen' : 'Mehr anzeigen'}
                </span>`;
            },
        },
        cssClasses: {
            label: ['form-checkbox'],
            checkbox: ['form-checkbox__input'],
            labelText: ['form-checkbox__text'],
        },
    });

/**
 * Create a toggle refinement panel.
 * @param {string} attribute        The attribute to refine.
 * @param {?array} relatedSections  Only show the panel if one of those sections is selected.
 */
const makeToggleRefinementPanel = (attribute, relatedSections = null) =>
    panel(makePanelConfig(attribute, relatedSections))(toggleRefinement)({
        attribute,
        container: document.querySelector(`[data-attribute="${attribute}"]`),
        on: 1,
        templates: {
            labelText(data, { html }) {
                return html`${de[`${attribute}.label`] || attribute}`;
            },
        },
        cssClasses: {
            label: ['form-checkbox'],
            checkbox: ['form-checkbox__input'],
            labelText: ['form-checkbox__text'],
        },
    });

search.use(clearInapplicableRefinementsOnSectionReset);
search.use(eventTrackingMiddlewareFactory(indexName, initialState));

search.addWidgets([
    configure({
        hitsPerPage: 5,
    }),

    panel({
        templates: {
            header(options, { html }) {
                return html`Inhaltstyp`;
            },
        },
    })(menuSelect)({
        container: document.querySelector('[data-attribute="section"]'),
        attribute: 'section',
        cssClasses: {
            select: ['form-select'],
        },
        templates: {
            defaultOption(data, { html }) {
                return html`– Alle –`;
            },
        },
        transformItems: items =>
            items.map(item => ({
                ...item,
                label: de[`section.${item.value}`] || item.label,
            })),
    }),

    makeRefinementListPanel('topics.title', null),
    makeRefinementListPanel('trade_sectors', null),
    makeRefinementListPanel('product_type', ['events', 'products']),
    makeToggleRefinementPanel('inhouse_available', ['products']),
    makeRefinementListPanel('target_groups', ['events', 'digital_products', 'products']),
    makeRefinementListPanel('event_format.label', ['events']),
    makeRefinementListPanel('digital_format', ['digital_products']),

    makeRefinementListPanel('podcast', ['episodes']),
    makeRefinementListPanel('episode_category', ['episodes']),

    makeRefinementListPanel('publications_category', ['publications']),

    searchBoxWithLabelWidget({
        container: '[data-widget="searchBox"]',
        label: 'Volltextsuche',
        placeholder: 'Suchbegriff',
        submitAriaLabel: 'Suchen',
    }),

    panel({
        templates: {
            header(options, { html }) {
                return html`Sortieren nach`;
            },
        },
    })(sortBy)({
        container: '[data-widget="sortBy"]',
        items: [
            { label: 'Empfohlene Reihenfolge', value: indexName },
            { label: 'Veranstaltungsdatum', value: `${indexName}:date_start:asc` },
            { label: 'Alphabetisch', value: `${indexName}:title:asc` },
        ],
        cssClasses: {
            select: ['form-select'],
        },
    }),

    currentRefinements({
        container: document.querySelector('[data-widget="currentRefinements"]'),
        transformItems: items =>
            items.map(item => ({
                ...item,
                refinements: item.refinements.map(refinement => ({
                    ...refinement,
                    label: de[`${refinement.attribute}.${refinement.value}`] || refinement.label,
                })),
            })),
    }),

    stats({
        container: document.querySelector('[data-widget="stats"]'),
        templates: {
            text({ nbHits }) {
                if (nbHits === 0) {
                    return '';
                }
                return `${nbHits} ${nbHits === 1 ? 'Ergebnis' : 'Ergebnisse'}`;
            },
        },
    }),

    clearRefinements({
        container: document.querySelector('[data-widget="clearRefinements"]'),
        templates: {
            resetLabel({ hasRefinements }, { html }) {
                return html`<span>${hasRefinements ? 'Alle Filter zurücksetzen' : ''}</span>`;
            },
        },
    }),

    infiniteHits({
        container: document.querySelector('[data-widget="infiniteHits"]'),
        templates: {
            item: (item, parameters) => itemTemplatesBySection[item.section](item, parameters),
            empty(results, { html }) {
                return html`<p class="alert alert--warning">Keine Suchergebnisse gefunden</p>`;
            },
            showMoreText: () => {
                return `Mehr Ergebnisse laden`;
            },
        },
    }),
]);

search.start();

const facetSearchToggleButton = document.querySelector('.facet-search__toggle-button');
const facetSearchActiveClassName = 'facet-search--filters-expanded';
const facetSearchToggleButtonActiveClassName = 'facet-search__toggle-button--active';

const showFacetSearchFilters = () => {
    document.querySelector('.facet-search').classList.add(facetSearchActiveClassName);
    facetSearchToggleButton.classList.add(facetSearchToggleButtonActiveClassName);
    facetSearchToggleButton.setAttribute('aria-expanded', 'true');
};

const hideFacetSearchFilters = () => {
    document.querySelector('.facet-search').classList.remove(facetSearchActiveClassName);
    facetSearchToggleButton.classList.remove(facetSearchToggleButtonActiveClassName);
    facetSearchToggleButton.setAttribute('aria-expanded', 'false');
};

facetSearchToggleButton.addEventListener('click', () => {
    const isActive = facetSearchToggleButton.getAttribute('aria-expanded') === 'true';
    isActive ? hideFacetSearchFilters() : showFacetSearchFilters();
});
